/**
 * Deduplicate items by name: keep one per name, preferring "in use" entries,
 * then tiebreaking by smaller id (string sort). Logs a console.warn when dropping a duplicate.
 */
export function deduplicateByName<T>(
  items: T[],
  options: {
    getName: (item: T) => string
    getId: (item: T) => string
    isInUse: (item: T) => boolean
    /** Label for id in warnings (e.g. "id", "eskId"). Default "id". */
    idLabel?: string
    /** Optional prefix for log messages (e.g. "[ESK] "). */
    logPrefix?: string
  },
): T[] {
  const { getName, getId, isInUse, idLabel = 'id', logPrefix = '' } = options
  const nameToWinner = new Map<string, T>()
  for (const item of items) {
    const name = getName(item)
    const existing = nameToWinner.get(name)
    if (!existing) {
      nameToWinner.set(name, item)
    } else {
      const existingInUse = isInUse(existing)
      const currentInUse = isInUse(item)
      let keepCurrent: boolean
      if (currentInUse && !existingInUse) {
        keepCurrent = true
        console.warn(
          `${logPrefix}Duplicate sidekick name "${name}" (${idLabel}: ${getId(existing)}). Keeping sponsor/preferred entry.`,
        )
      } else if (!currentInUse && existingInUse) {
        keepCurrent = false
        console.warn(
          `${logPrefix}Duplicate sidekick name "${name}" (${idLabel}: ${getId(item)}). Keeping sponsor/preferred entry.`,
        )
      } else {
        const existingId = getId(existing)
        const currentId = getId(item)
        keepCurrent = currentId.localeCompare(existingId) < 0
        console.warn(
          `${logPrefix}Duplicate sidekick name "${name}" (${idLabel}: ${keepCurrent ? getId(existing) : getId(item)}). Keeping preferred entry.`,
        )
      }
      if (keepCurrent) {
        nameToWinner.set(name, item)
      }
    }
  }
  return items.filter(item => nameToWinner.get(getName(item)) === item)
}
